"use client";

import React, { useEffect, useState } from "react";

import { FeatureFlags } from "@/enums/feature-flags";
import { AdminRole, UserRole } from "@/enums/roles.enum";
import { useFeatureFlag } from "@/hooks/useFeatureFlag";
import { useSession } from "@/providers/SessionProvider";
import { cn } from "@/utils/cn";

export interface RealtorWrapperProps {
  /** Content to show for realtors */
  children: React.ReactNode;
  /** CSS class name for custom styling */
  className?: string;
  /** Whether to show debug info in development */
  showDebug?: boolean;
  /** Custom role check function */
  customRoleCheck?: (hasRole: (role: string) => boolean, user: any) => boolean;
  /** Props for fallback ObjectDetailActions component */
  fallbackContent?: React.ReactNode | null;
}

/**
 * RealtorWrapper - HOC for conditional realtor content display
 *
 * Features:
 * - Progressive Enhancement with ISR compatibility
 * - Server-side rendering shows fallback content
 * - Client-side hydration adds functionality for authenticated realtors
 * - Prevents layout shift during hydration
 *
 * Authentication Logic:
 * - Shows children only for realtor-capable roles (user_realtor, realtor, main_realtor, admin, super_admin)
 * - Shows fallback for other users
 * - Uses client-side hydration to prevent SSR/client mismatch
 *
 * @example
 * ```tsx
 * <RealtorWrapper
 *   fallbackContent={<CustomFallbackComponent />}
 * >
 *   <PropertyActionButtons propertyId="123" />
 * </RealtorWrapper>
 * ```
 */
export const RealtorWrapper: React.FC<RealtorWrapperProps> = ({
  children,
  className,
  customRoleCheck,
  fallbackContent = null,
}) => {
  const { isAuthenticated, hasRole, user } = useSession();
  const [isHydrated, setIsHydrated] = useState(false);

  // Progressive enhancement - client side hydration
  useEffect(() => {
    setIsHydrated(true);
  }, []);

  // Before hydration show fallback to prevent layout shift
  if (!isHydrated) {
    return <div className={cn("min-h-[54px]", className)}>{fallbackContent}</div>;
  }

  // After hydration check authentication and role
  const isRealtor = customRoleCheck 
    ? customRoleCheck(hasRole, user)
    : isAuthenticated && (
        hasRole('user_realtor') || 
        hasRole('USER_REALTOR') || 
        hasRole('main_realtor') ||
        hasRole('super_admin') ||
        hasRole('admin') ||
        hasRole('ADMIN')
      );

  // If not realtor - show fallback
  if (!isRealtor) {
    return <div className={className}>{fallbackContent}</div>;
  }

  // Realtor authenticated - show realtor content
  return (
    <div className={className}>{children}</div>
  );
};

export default RealtorWrapper;
